import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared axios instance that carries EITHER the auth bearer OR a guest session id.
const shopClient = axios.create({ baseURL: API });

function ensureSessionId() {
  let sid = localStorage.getItem("ba_shop_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("ba_shop_sid", sid);
  }
  return sid;
}

shopClient.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("ba_token");
  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
  } else {
    cfg.headers["X-Session-Id"] = ensureSessionId();
  }
  return cfg;
});

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], subtotal: 0, count: 0 });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await shopClient.get("/shop/cart");
      setCart(data);
    } catch (e) {
      // fail-silent — cart may not exist yet
      setCart({ items: [], subtotal: 0, count: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Expose refresh globally so AuthContext can trigger it right after login/logout
  // (avoids fragile polling and race conditions).
  useEffect(() => {
    window.__ba_cart_refresh__ = refresh;
    return () => { delete window.__ba_cart_refresh__; };
  }, [refresh]);

  const addToCart = useCallback(async (productId, quantity = 1, options = {}, extras = {}) => {
    const { data } = await shopClient.post("/shop/cart/add", {
      product_id: productId,
      quantity,
      options,
      design_choice: extras.design_choice || "",
      design_files: extras.design_files || [],
      design_notes: extras.design_notes || "",
    });
    setCart(data);
    return data;
  }, []);

  const updateItem = useCallback(async (lineId, quantity) => {
    const { data } = await shopClient.patch("/shop/cart/item", { product_id: "", line_id: lineId, quantity });
    setCart(data);
    return data;
  }, []);

  const removeItem = useCallback(async (lineId) => {
    const { data } = await shopClient.delete(`/shop/cart/item/${lineId}`);
    setCart(data);
    return data;
  }, []);

  const clearCart = useCallback(async () => {
    await shopClient.post("/shop/cart/clear");
    setCart({ items: [], subtotal: 0, count: 0 });
  }, []);

  const checkout = useCallback(async (payload) => {
    const { data } = await shopClient.post("/shop/checkout", payload);
    setCart({ items: [], subtotal: 0, count: 0 });
    return data;
  }, []);

  const validateCoupon = useCallback(async (code, subtotal) => {
    const { data } = await shopClient.post("/shop/coupon/validate", { code, subtotal });
    return data;
  }, []);

  const validateGiftCard = useCallback(async (code) => {
    const { data } = await shopClient.post("/shop/giftcard/validate", { code });
    return data;
  }, []);

  const value = useMemo(
    () => ({ cart, loading, refresh, addToCart, updateItem, removeItem, clearCart, checkout, validateCoupon, validateGiftCard, shopClient }),
    [cart, loading, refresh, addToCart, updateItem, removeItem, clearCart, checkout, validateCoupon, validateGiftCard]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};

export { shopClient };
